'use client';

import useSWR from 'swr';
import type { ScopedMutator } from 'swr';
import { categoryBudgetApi } from '@/lib/api';
import type { ReviewQueueResponse } from '@/types';
import {
  TRANSACTION_SWR_OPTIONS,
  swrKeyStartsWith,
  useAuthedFetcher,
} from './swrConfig';

export type ReviewQueueKind = 'category' | 'refund' | 'investment';

/**
 * Cache families a refund mutation invalidates beyond the transaction caches:
 * home widgets AND the /budget category overview — both derive from budget
 * eligibility/totals the change just restamped. Never includes review-queue
 * itself.
 */
export const POST_REFUND_KEY_PREFIXES = ['home-widget', 'category-overview'] as const;

/**
 * The one list the refund mutation paths (link/relink/unlink/flag/unflag on
 * any host) refresh through, so the hosts can never drift apart.
 */
export function revalidatePostRefundCaches(mutateGlobal: ScopedMutator): void {
  void mutateGlobal((key) => swrKeyStartsWith(key, POST_REFUND_KEY_PREFIXES));
}

/**
 * The "A revisar" queue is period-independent — every pending item, any date.
 * `kind` scopes the queue (and its total) to one review dimension: /budget asks
 * for 'category', /transactions for 'refund', home omits it for the full count.
 */
export function useReviewQueue(kind?: ReviewQueueKind) {
  const authedFetch = useAuthedFetcher();

  return useSWR<ReviewQueueResponse>(
    ['review-queue', kind ?? 'all'],
    () => authedFetch((token) => categoryBudgetApi.getReviewQueue(token, kind)),
    TRANSACTION_SWR_OPTIONS,
  );
}
